import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSpring, animated } from 'react-spring';
import { usePinch } from '@use-gesture/react';
import { api } from '@/api';
import { LoadingScreen } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { cn, getBadgeLevelColor, getScoreColor, capitalizeBadgeLevel, Linkify } from '@/utils';
import type { FullProfile } from '@/types';
import ReportModal from '@/components/modals/ReportModal';
import BlockConfirmModal from '@/components/modals/BlockConfirmModal';

interface ProfileDetailScreenProps {
  userId: string;
  onBack: () => void;
  onSwipe: (direction: 'like' | 'pass') => void;
  matchId?: string; // Optional: if viewing from a match/chat
  onUnmatch?: () => void; // Optional: callback after successful unmatch
}

const ProfileDetailScreen: React.FC<ProfileDetailScreenProps> = ({ userId, onBack, onSwipe, matchId, onUnmatch }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isUnmatching, setIsUnmatching] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['profile', userId],
    queryFn: () => api.getProfile(userId),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Preload all photos for smooth navigation
  useEffect(() => {
    if (profile?.photos) {
      profile.photos.forEach(photoUrl => {
        const img = new Image();
        img.src = photoUrl;
      });
    }
  }, [profile?.photos]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  if (isLoading) {
    return <LoadingScreen message="Загрузка профиля..." />;
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-white mb-4">Не удалось загрузить профиль</p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-white text-black rounded-2xl font-bold"
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) =>
      prev < profile.photos.length - 1 ? prev + 1 : 0
    );
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) =>
      prev > 0 ? prev - 1 : profile.photos.length - 1
    );
  };

  const handleSwipe = (direction: 'like' | 'pass') => {
    TelegramUtils.haptic.light();
    onSwipe(direction);
  };

  const handleUnmatch = async () => {
    if (!matchId) return;

    const confirmed = await TelegramUtils.showConfirm(
      `Вы уверены, что хотите удалить ${profile?.name} из совпадений?`
    );

    if (!confirmed) return;

    setIsUnmatching(true);
    try {
      console.log('🗑️ Deleting match:', matchId);
      
      // Use the correct API method with number matchId
      const result = await (api as any).unmatchUser(Number(matchId));
      
      console.log('✅ Match deleted successfully:', result);
      TelegramUtils.haptic.success();
      
      // Invalidate matches and messages queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['matches'] });
      await queryClient.invalidateQueries({ queryKey: ['messages', matchId] });
      
      // Refetch matches to update the list
      await queryClient.refetchQueries({ queryKey: ['matches'] });
      
      await TelegramUtils.showAlert('Совпадение удалено');
      
      // Call onUnmatch callback if provided, otherwise use onBack
      if (onUnmatch) {
        onUnmatch();
      } else {
        onBack();
      }
    } catch (error: any) {
      console.error('❌ Failed to unmatch:', error);
      TelegramUtils.haptic.error();
      await TelegramUtils.showAlert('Не удалось удалить совпадение. Попробуйте позже.');
    } finally {
      setIsUnmatching(false);
    }
  };

  const handleMenuToggle = () => {
    TelegramUtils.haptic.selection();
    setShowMenu(!showMenu);
  };

  const handleReportClick = () => {
    setShowMenu(false);
    setShowReportModal(true);
  };

  const handleBlockClick = () => {
    setShowMenu(false);
    setShowBlockModal(true);
  };

  const handleBlockConfirm = () => {
    setShowBlockModal(false);
    // Navigate back after blocking
    onBack();
  };

  return (
    <div className="h-screen bg-tg-bg flex flex-col safe-area-y">
      {/* Header with back button and menu */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 bg-tg-bg/95 backdrop-blur-xl border-b border-white/5 relative z-50">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Назад</span>
        </button>

        {/* Menu button */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={handleMenuToggle}
            className="flex items-center justify-center w-10 h-10 text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="6" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="18" r="2" />
            </svg>
          </button>

          {/* Dropdown menu */}
          {showMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-tg-secondary-bg border border-white/10 rounded-xl shadow-xl overflow-hidden animate-scale-in z-[100]">
              <button
                onClick={handleReportClick}
                className="w-full px-4 py-3 flex items-center gap-3 text-left text-white hover:bg-white/5 transition-colors"
              >
                <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>Пожаловаться</span>
              </button>

              <div className="h-px bg-white/10" />

              <button
                onClick={handleBlockClick}
                className="w-full px-4 py-3 flex items-center gap-3 text-left text-danger-400 hover:bg-danger-500/10 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                <span>Заблокировать</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Photo Gallery */}
        <div className="relative w-full aspect-[3/4] max-h-[600px] bg-neutral-900">
        <ZoomableImage
          src={profile.photos[currentPhotoIndex]}
          alt={profile.name}
          onNavigationEnabled={(enabled) => {
            // Disable photo navigation when zoomed
            if (!enabled) {
              // User is zooming, disable navigation
            }
          }}
        />

        {/* Photo indicators */}
        {profile.photos.length > 1 && (
          <div className="absolute top-4 left-4 right-4 flex gap-1 z-10 pointer-events-none">
            {profile.photos.map((_, index) => (
              <div
                key={index}
                className={cn(
                  'flex-1 h-1 rounded-full transition-colors',
                  index === currentPhotoIndex ? 'bg-white' : 'bg-white/30'
                )}
              />
            ))}
          </div>
        )}

        {/* Photo navigation */}
        {profile.photos.length > 1 && (
          <>
            <button
              className="absolute left-0 top-0 bottom-0 w-1/2 z-20"
              onClick={prevPhoto}
            />
            <button
              className="absolute right-0 top-0 bottom-0 w-1/2 z-20"
              onClick={nextPhoto}
            />
          </>
        )}
        </div>

        {/* Profile Info */}
        <div className="p-6 space-y-6 pb-32">
        {/* Name, Age, Status */}
        <div>
          <div className="flex items-baseline gap-3">
            <h1 className="text-4xl font-bold text-white">
              {profile.name}
            </h1>
            <span className="text-3xl text-white/80">
              {profile.age}
            </span>
          </div>
        </div>

        {/* IT Info - if exists */}
        {profile.role && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {profile.level && (
                <span className={cn(
                  'px-3 py-1 rounded-full text-sm font-semibold',
                  getBadgeLevelColor(profile.level)
                )}>
                  {capitalizeBadgeLevel(profile.level)}
                </span>
              )}
              {profile.verified && profile.itTestScore !== undefined && (
                <span className={cn(
                  'px-3 py-1 rounded-full text-sm font-bold',
                  profile.itTestScore < 50 
                    ? 'bg-white/10 text-white'
                    : 'bg-primary-500/20 border border-primary-500/30 text-primary-300'
                )}>
                  {profile.itTestScore} баллов
                </span>
              )}
            </div>
            <p className="text-lg text-white font-medium">
              {profile.role}
            </p>
            {profile.company && (
              <p className="text-sm text-neutral-400 mt-1">
                {profile.company}
              </p>
            )}
          </div>
        )}

        {/* Bio */}
        {profile.bio && (
          <div>
            <h2 className="text-lg font-bold text-white mb-2">О себе</h2>
            <p className="text-neutral-300 leading-relaxed whitespace-pre-wrap">
              <Linkify>{profile.bio}</Linkify>
            </p>
          </div>
        )}

        {/* Desired Lifestyle */}
        {profile.desiredLifestyle && (
          <div>
            <h2 className="text-lg font-bold text-white mb-3">Искомый стиль жизни</h2>
            <span className="inline-block px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white">
              {profile.desiredLifestyle}
            </span>
          </div>
        )}

        {/* Location */}
        {(profile.city || profile.country) && (
          <div>
            <h2 className="text-lg font-bold text-white mb-3">Местоположение</h2>
            <p className="text-neutral-300">
              📍 {[profile.city, profile.country].filter(Boolean).join(', ')}
            </p>
          </div>
        )}

        {/* Meeting Goals */}
        {profile.meetingGoals && profile.meetingGoals.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-white mb-3">Цели знакомства</h2>
            <div className="flex flex-wrap gap-2">
              {profile.meetingGoals.map((goal) => (
                <span
                  key={goal.id}
                  className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-sm text-white"
                >
                  {goal.icon} {goal.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tech Stack */}
        {profile.techStack && profile.techStack.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-white mb-3">Tech Stack</h2>
            <div className="flex flex-wrap gap-2">
              {profile.techStack.map((tech) => (
                <span
                  key={tech}
                  className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-sm text-white"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Hobbies */}
        {profile.lifestyle && profile.lifestyle.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-white mb-3">Интересы</h2>
            <div className="flex flex-wrap gap-2">
              {profile.lifestyle.map((hobby) => (
                <span
                  key={hobby}
                  className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-sm text-neutral-300"
                >
                  {hobby}
                </span>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Action Buttons - Fixed at bottom */}
      <div className="flex-shrink-0 safe-area-bottom">
        <div className="flex items-center justify-center gap-3 p-4 bg-tg-bg/95 backdrop-blur-xl border-t border-white/5">
          {matchId ? (
            // Show unmatch button when viewing from chat/match
            <div className="w-full max-w-sm">
              <button
                onClick={handleUnmatch}
                disabled={isUnmatching}
                className="w-full h-14 bg-danger-500/20 border-2 border-danger-500 rounded-2xl text-danger-500 font-bold hover:bg-danger-500/30 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUnmatching ? 'Удаление...' : 'Удалить из совпадений'}
              </button>
            </div>
          ) : (
            // Show swipe buttons when viewing from feed
            <div className="flex items-center justify-center gap-3 w-full max-w-sm">
              <button
                onClick={() => handleSwipe('pass')}
                className="flex-1 h-14 bg-white/10 border border-white/20 rounded-2xl text-white font-semibold hover:bg-white/20 transition-all active:scale-95"
              >
                Пропустить
              </button>

              <button
                onClick={() => handleSwipe('like')}
                className="flex-1 h-14 bg-white text-black rounded-2xl font-bold hover:bg-neutral-100 transition-all active:scale-95"
              >
                ❤️ Нравится
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showReportModal && profile && (
        <ReportModal
          userId={userId}
          userName={profile.name}
          onClose={() => setShowReportModal(false)}
        />
      )}

      {showBlockModal && profile && (
        <BlockConfirmModal
          userId={userId}
          userName={profile.name}
          matchId={matchId}
          onClose={() => setShowBlockModal(false)}
          onConfirm={handleBlockConfirm}
        />
      )}
    </div>
  );
};

// ZoomableImage component with pinch-to-zoom support
interface ZoomableImageProps {
  src: string;
  alt: string;
  onNavigationEnabled?: (enabled: boolean) => void;
}

const ZoomableImage: React.FC<ZoomableImageProps> = ({ src, alt, onNavigationEnabled }) => {
  const [isZooming, setIsZooming] = useState(false);
  const [{ scale }, api] = useSpring(() => ({
    scale: 1,
    config: { tension: 300, friction: 30 },
  }));

  const bind = usePinch(
    ({ offset: [s], last, active }) => {
      // Clamp scale between 1 and 3
      const clampedScale = Math.max(1, Math.min(s, 3));
      
      // Set zooming state - true when actively pinching or when zoomed > 1
      setIsZooming(active || clampedScale > 1);
      
      api.start({ 
        scale: clampedScale,
      });

      // If user released and scale is close to 1, snap back to 1
      if (last && clampedScale < 1.2) {
        api.start({ scale: 1 });
        setIsZooming(false);
      }

      // Notify parent about navigation state
      if (onNavigationEnabled) {
        onNavigationEnabled(clampedScale <= 1);
      }
    },
    {
      scaleBounds: { min: 1, max: 3 },
      rubberband: true,
    }
  );

  // Reset zoom when image changes
  useEffect(() => {
    api.start({ scale: 1 });
    setIsZooming(false);
  }, [src, api]);

  return (
    <animated.div
      {...bind()}
      className="w-full h-full"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Allow vertical scroll when not zooming, disable touch when zooming
        touchAction: isZooming ? 'none' : 'pan-y',
        overflow: 'hidden',
      }}
    >
      <animated.img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        style={{
          scale,
          transformOrigin: 'center center',
        }}
      />
    </animated.div>
  );
};

export default ProfileDetailScreen;

